//src/pages/admin/tabs/managers/ManagerEditPage.jsx

import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import { Card, Row2, Field, Actions, Btn, Badge } from '../../ui';
import MediaPickerModal from '../../components/MediaPickerModal';

function parseCSVtoIds(s){
  return String(s || '')
    .split(',')
    .map(x => Number(String(x).trim()))
    .filter(n => Number.isFinite(n) && n > 0);
}
const nonEmpty = (x)=> x!=null && String(x).trim()!==''; 

export default function ManagerEditPage(){
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();

  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [ok, setOk] = React.useState('');

  // основные поля
  const [first_name_ru, setFru] = React.useState('');
  const [first_name_en, setFen] = React.useState('');
  const [first_name_zh, setFzh] = React.useState('');
  const [last_name_ru,  setLru] = React.useState('');
  const [last_name_en,  setLen] = React.useState('');
  const [last_name_zh,  setLzh] = React.useState('');
  const [position_ru,   setPosRu] = React.useState('');
  const [position_en,   setPosEn] = React.useState('');
  const [position_zh,   setPosZh] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [phone_secondary, setPhone2] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [whatsapp, setWhatsapp] = React.useState('');
  const [telegram, setTelegram] = React.useState('');
  const [bio_ru, setBioRu] = React.useState('');
  const [bio_en, setBioEn] = React.useState('');
  const [bio_zh, setBioZh] = React.useState('');
  const [experience_years, setExp] = React.useState(0);
  const [languages, setLangs] = React.useState(''); // строка "ru,en"
  const [regions, setRegions] = React.useState([]); // список регионов для выбора
  const [selectedRegionIds, setSelectedRegionIds] = React.useState([]); // массив чисел
  const [is_active, setActive] = React.useState(true);

  // аватар (предпросмотр + id)
  const [avatarPreview, setAvatarPreview] = React.useState('');
  const [avatar_media_id, setAvatarMediaId] = React.useState(undefined);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const [uploadBusy, setUploadBusy] = React.useState(false);

  // загрузка при редактировании
  React.useEffect(()=>{
    let stop=false;
    (async()=>{
      if(!isEdit) return;
      try{
        const d = await AdminAPI.getManager(id, { locale: 'ru' });
        const m = d?.manager || d?.item || d;
        if (stop || !m) return;
        setFru(m.first_name ?? m.first_name_ru ?? ''); setFen(m.first_name_en ?? ''); setFzh(m.first_name_zh ?? '');
        setLru(m.last_name ?? m.last_name_ru ?? '');   setLen(m.last_name_en ?? '');  setLzh(m.last_name_zh ?? '');
        setPosRu(m.position ?? m.position_ru ?? '');   setPosEn(m.position_en ?? ''); setPosZh(m.position_zh ?? '');
        setPhone(m.phone || ''); setPhone2(m.phone_secondary || '');
        setEmail(m.email || ''); setWhatsapp(m.whatsapp || ''); setTelegram(m.telegram || '');
        setBioRu(m.bio_ru || ''); setBioEn(m.bio_en || ''); setBioZh(m.bio_zh || '');
        setExp(Number(m.experience_years || 0));
        const langs = Array.isArray(m.languages) ? m.languages.join(',') : (m.languages || '');
        setLangs(langs);
        const rids = (m.regions?.map?.(r=>r.id) || m.region_ids || []);
        setSelectedRegionIds(Array.isArray(rids) ? rids.map(Number) : parseCSVtoIds(rids));
        setActive(m.is_active ?? true);
        setAvatarPreview(m.avatar_thumb || m.avatar_url || m.avatar || '');
        setAvatarMediaId(m.avatar_media?.id || undefined);
      }catch(e){ setErr(e?.payload?.error?.message || e?.payload?.message || e?.message || 'Не удалось загрузить менеджера'); }
    })();
    return ()=>{ stop=true; };
  }, [isEdit, id]);

  // загрузить регионы для селекта
  React.useEffect(()=>{
    let stop=false;
    (async()=>{
      try{
        const r = await PublicAPI.regions({ locale: 'ru' });
        if (stop) return;
        setRegions(r?.regions || []);
      }catch{
        if (!stop) setRegions([]);
      }
    })();
    return ()=>{ stop=true; };
  },[]);

  const onPickFromLibrary = (list)=>{
    const file = list?.[0]; if (!file) return;
    setAvatarMediaId(file.id);
    setAvatarPreview(file.thumbnail_url || file.url || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const uploadFile = async (file)=>{
    if(!file) return;
    setUploadBusy(true); setErr('');
    try{
      const up = await AdminAPI.uploadMedia(file);
      const f  = up?.file || up;
      if(!f?.id) throw new Error('Медиафайл не вернул id');
      setAvatarMediaId(f.id);
      setAvatarPreview(f.thumbnail_url || f.url || '');
      setOk('Фото загружено');
    }catch(e){ setErr(e?.message || 'Не удалось загрузить файл'); }
    finally{
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const clearAvatar = ()=>{ setAvatarMediaId(undefined); setAvatarPreview(''); if (fileInputRef.current) fileInputRef.current.value=''; };

  const submit = async ()=>{
    setSaving(true); setErr(''); setOk('');
    try{
      // обязательные поля по спецификации
      if (!nonEmpty(first_name_ru) && !nonEmpty(first_name_en)) throw new Error('Укажите имя (RU или EN)');
      if (!nonEmpty(last_name_ru)  && !nonEmpty(last_name_en))  throw new Error('Укажите фамилию (RU или EN)');
      if (!nonEmpty(position_ru)   && !nonEmpty(position_en))   throw new Error('Укажите должность (RU или EN)');
      if (!nonEmpty(phone)) throw new Error('Укажите телефон');

      const payload = {
        first_name_ru: nonEmpty(first_name_ru) ? first_name_ru : undefined,
        first_name_en: nonEmpty(first_name_en) ? first_name_en : undefined,
        first_name_zh: nonEmpty(first_name_zh) ? first_name_zh : undefined,
        last_name_ru:  nonEmpty(last_name_ru)  ? last_name_ru  : undefined,
        last_name_en:  nonEmpty(last_name_en)  ? last_name_en  : undefined,
        last_name_zh:  nonEmpty(last_name_zh)  ? last_name_zh  : undefined,
        position_ru:   nonEmpty(position_ru)   ? position_ru   : undefined,
        position_en:   nonEmpty(position_en)   ? position_en   : undefined,
        position_zh:   nonEmpty(position_zh)   ? position_zh   : undefined,
        phone: phone,
        phone_secondary: nonEmpty(phone_secondary) ? phone_secondary : undefined,
        email: nonEmpty(email) ? email : undefined,
        whatsapp: nonEmpty(whatsapp) ? whatsapp : undefined,
        telegram: nonEmpty(telegram) ? telegram : undefined,
        bio_ru: nonEmpty(bio_ru) ? bio_ru : undefined,
        bio_en: nonEmpty(bio_en) ? bio_en : undefined,
        bio_zh: nonEmpty(bio_zh) ? bio_zh : undefined,
        experience_years: Number(experience_years) || 0,
        languages: nonEmpty(languages) ? languages.split(',').map(s=>s.trim()).filter(Boolean) : undefined,
        region_ids: Array.isArray(selectedRegionIds) && selectedRegionIds.length ? selectedRegionIds.map(Number) : [],
        is_active: !!is_active,
        avatar_media_id: avatar_media_id ?? undefined,
        // legacy URL можно отправить как avatar, если картинку подставили вручную:
        avatar: (!avatar_media_id && avatarPreview) ? avatarPreview : undefined,
      };

      if (isEdit) {
        await AdminAPI.updateManager(id, payload);
        setOk('Сохранено');
      } else {
        const res = await AdminAPI.createManager(payload);
        const newId = res?.manager?.id || res?.item?.id || res?.id;
        setOk('Создано');
        if (newId) { nav(`/admin/managers/${newId}`); return; }
      }
    }catch(e){
      setErr(
      e?.payload?.details
        ? (typeof e.payload.details === 'string' ? e.payload.details : JSON.stringify(e.payload.details))
        : (e?.payload?.error?.message || e?.payload?.message || e?.message || 'Ошибка сохранения')
      );
    }finally{ setSaving(false); }
  };

  return (
    <>
      <Actions style={{marginBottom:12}}>
        <Link to="/admin/managers" className="btn">← К списку менеджеров</Link>
      </Actions>

      <Card>
        <h3>{isEdit ? 'Редактировать менеджера' : 'Создать менеджера'}</h3>
        {err && (
        <div style={{color:'#e66', marginBottom:8}}>
            {typeof err === 'string'
            ? err
            : (err?.message || err?.error || JSON.stringify(err))}
        </div>
        )}
        {ok  && <div style={{color:'#2a8f2a', marginBottom:8}}>{ok}</div>}

        <Row2>
          <Field>Имя RU<input value={first_name_ru} onChange={e=>setFru(e.target.value)} /></Field>
          <Field>Имя EN<input value={first_name_en} onChange={e=>setFen(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Имя (中文)<input value={first_name_zh} onChange={e=>setFzh(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Фамилия RU<input value={last_name_ru} onChange={e=>setLru(e.target.value)} /></Field>
          <Field>Фамилия EN<input value={last_name_en} onChange={e=>setLen(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Фамилия (中文)<input value={last_name_zh} onChange={e=>setLzh(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Должность RU<input value={position_ru} onChange={e=>setPosRu(e.target.value)} /></Field>
          <Field>Должность EN<input value={position_en} onChange={e=>setPosEn(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Должность (中文)<input value={position_zh} onChange={e=>setPosZh(e.target.value)} /></Field>
        </Row2>

        <Row2>
          <Field>Телефон<input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+971..." /></Field>
          <Field>Доп. телефон<input value={phone_secondary} onChange={e=>setPhone2(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Email<input value={email} onChange={e=>setEmail(e.target.value)} /></Field>
          <Field>Telegram<input value={telegram} onChange={e=>setTelegram(e.target.value)} placeholder="@username или t.me/..." /></Field>
        </Row2>
        <Row2>
          <Field>Telegram<input value={telegram} onChange={e=>setTelegram(e.target.value)} placeholder="@username" /></Field>
          <Field>Опыт (лет)<input type="number" value={experience_years} onChange={e=>setExp(e.target.value)} /></Field>
        </Row2>
        <Row2>
          <Field>Языки (через запятую)<input value={languages} onChange={e=>setLangs(e.target.value)} placeholder="ru,en,ar" /></Field>
          <div style={{ display:'grid', gap:6, fontSize:14 }}>
            Регионы
            <div style={{ display:'flex', gap:12, alignItems:'flex-start', flexWrap:'wrap' }}>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontWeight:700, marginBottom:6 }}>Доступные</div>
                <div style={{ display:'grid', gap:6, maxHeight:200, overflow:'auto', border:'1px solid var(--bdr)', borderRadius:10, padding:8 }}>
                  {regions.filter(r=>!selectedRegionIds.includes(Number(r.id))).map(r=>(
                    <div key={r.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8 }}>
                      <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {r.name || r.name_ru || r.title || r.slug}
                      </span>
                      <Btn
                        type="button"
                        onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedRegionIds(ids=> Array.from(new Set([...(ids||[]), Number(r.id)]))); }}
                        aria-label={`Добавить регион ${r.name || r.name_ru || r.title || r.slug}`}
                      >+</Btn>
                    </div>
                  ))}
                  {regions.filter(r=>!selectedRegionIds.includes(Number(r.id))).length===0 && (
                    <div style={{ opacity:.7 }}>Все регионы выбраны</div>
                  )}
                </div>
              </div>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontWeight:700, marginBottom:6 }}>Выбраны</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', border:'1px solid var(--bdr)', borderRadius:10, padding:8, minHeight:44 }}>
                  {(selectedRegionIds || []).length===0 && <div style={{opacity:.7}}>Пока пусто</div>}
                  {(selectedRegionIds || []).map(id=>{
                    const r = regions.find(x=>Number(x.id)===Number(id));
                    const label = r?.name || r?.name_ru || r?.title || r?.slug || String(id);
                    return (
                      <span key={id} className="badge" style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                        {label}
                        <button
                          type="button"
                          className="btn"
                          onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); setSelectedRegionIds(arr=> (arr||[]).filter(x=>Number(x)!==Number(id))); }}
                                aria-label="Удалить регион">×</button>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            <small>Нажмите “+” чтобы добавить регион; для удаления — “×”.</small>
          </div>
        </Row2>

        <Row2>
          <Field>Активен
            <select value={is_active ? '1' : '0'} onChange={e=>setActive(e.target.value==='1')}>
              <option value="1">Да</option>
              <option value="0">Нет</option>
            </select>
          </Field>
        </Row2>

        <hr style={{border:'none', borderTop:'1px dashed var(--bdr)', margin:'12px 0'}} />

        <h3>Фото (аватар)</h3>
        {avatarPreview && (
          <div style={{marginBottom:8}}>
            <img src={avatarPreview} alt="" style={{width:120, height:120, objectFit:'cover', borderRadius:12, border:'1px solid var(--bdr)'}} />
            {avatar_media_id && <div style={{fontSize:12, opacity:.7, marginTop:4}}>media_id: <b>{avatar_media_id}</b></div>}
          </div>
        )}
        <Actions style={{gap:10, flexWrap:'wrap'}}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{display:'none'}}
            onChange={(e)=>uploadFile(e.target.files?.[0])}
          />
          <Btn onClick={()=>fileInputRef.current?.click()} disabled={uploadBusy}>
            {uploadBusy ? 'Загрузка…' : 'Загрузить файл'}
          </Btn>
          <Btn onClick={()=>setPickerOpen(true)} disabled={uploadBusy}>Выбрать из медиатеки</Btn>
          {avatarPreview && <Btn onClick={clearAvatar} disabled={uploadBusy}>Убрать</Btn>}
        </Actions>

        <Actions style={{marginTop:14}}>
          <Btn $kind="primary" onClick={submit} disabled={saving}>
            {saving ? 'Сохранение…' : (isEdit ? 'Сохранить' : 'Создать')}
          </Btn>
          <Link to="/admin/managers" className="btn">Отмена</Link>
          <Badge style={{marginLeft:'auto'}}>Подсказка: languages вводятся через запятую.</Badge>
        </Actions>
      </Card>

      <MediaPickerModal open={pickerOpen} onClose={()=>setPickerOpen(false)} onPick={onPickFromLibrary} multi={false} />
    </>
  );
}
